import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllParties,
  getMyParties,
  createParty,
  updateParty,
  deleteParty,
  searchParties,
  clearErrors,
  clearMessage,
  resetSuccess,
} from "../../redux/partySlice";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Helmet } from "react-helmet-async";

const Party = () => {
  const dispatch = useDispatch();
  const { parties, loading, error, success, message } = useSelector(
    (state) => state.party
  );
  const { user } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    name: "",
    number: "",
    duePayment: "",
    totalPayment: "",
    advanceAmount: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    number: "",
    duePayment: "",
    totalPayment: "",
    advanceAmount: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    number: false,
    duePayment: false,
    totalPayment: false,
    advanceAmount: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showMyParties, setShowMyParties] = useState(false);
  const [dateRange, setDateRange] = useState("all"); // all, thisMonth, lastMonth, custom
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredParties, setFilteredParties] = useState([]);

  const formatMoney = (val) => `Rs.${(parseFloat(val) || 0).toFixed(2)}`;
  const formatDate = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDateRanges = () => {
    const today = new Date();
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    return {
      thisMonth: { start: thisMonthStart, end: thisMonthEnd },
      lastMonth: { start: lastMonthStart, end: lastMonthEnd },
    };
  };

  // Validation functions
  const validateName = (name) => {
    if (!name || !name.trim()) {
      return "Party name is required";
    }
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      return "Party name must be at least 2 characters";
    }
    if (trimmed.length > 100) {
      return "Party name cannot exceed 100 characters";
    }
    return "";
  };

  const validateNumber = (number) => {
    if (!number || !number.trim()) {
      return "Party number is required";
    }
    const digitsOnly = number.replace(/\D/g, "");
    if (digitsOnly.length !== 10) {
      return "Party number must be exactly 10 digits";
    }
    return "";
  };

  const validateAmount = (amount, fieldName) => {
    if (!amount && amount !== 0) {
      return `${fieldName} is required`;
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) {
      return `${fieldName} must be a valid number`;
    }
    if (amountNum < 0) {
      return `${fieldName} cannot be negative`;
    }
    if (!Number.isFinite(amountNum)) {
      return `${fieldName} must be a finite number`;
    }
    return "";
  };

  const validateForm = () => {
    const nameValid = validateField("name", formData.name);
    const numberValid = validateField("number", formData.number);
    const duePaymentValid = validateField("duePayment", formData.duePayment);
    const totalPaymentValid = validateField("totalPayment", formData.totalPayment);
    const advanceAmountValid = validateField("advanceAmount", formData.advanceAmount);

    setTouched({
      name: true,
      number: true,
      duePayment: true,
      totalPayment: true,
      advanceAmount: true,
    });

    // Business logic validation
    if (nameValid === "" && numberValid === "" && duePaymentValid === "" && 
        totalPaymentValid === "" && advanceAmountValid === "") {
      const totalPaymentNum = parseFloat(formData.totalPayment);
      const advanceAmountNum = parseFloat(formData.advanceAmount);
      
      if (advanceAmountNum > totalPaymentNum) {
        setErrors((prev) => ({
          ...prev,
          advanceAmount: "Advance amount cannot exceed total payment",
        }));
        return false;
      }
    }

    return nameValid === "" && numberValid === "" && duePaymentValid === "" && 
           totalPaymentValid === "" && advanceAmountValid === "";
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "name":
        error = validateName(value);
        break;
      case "number":
        error = validateNumber(value);
        break;
      case "duePayment":
        error = validateAmount(value, "Due payment");
        break;
      case "totalPayment":
        error = validateAmount(value, "Total payment");
        break;
      case "advanceAmount":
        error = validateAmount(value, "Advance amount");
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  // Load parties on component mount
  useEffect(() => {
    if (showMyParties && user) {
      dispatch(getMyParties());
    } else {
      dispatch(getAllParties());
    }
  }, [dispatch, showMyParties, user]);

  // Filter parties based on date range
  useEffect(() => {
    const ranges = getDateRanges();
    let start = null;
    let end = null;

    if (dateRange === "thisMonth") {
      start = ranges.thisMonth.start;
      end = ranges.thisMonth.end;
    } else if (dateRange === "lastMonth") {
      start = ranges.lastMonth.start;
      end = ranges.lastMonth.end;
    } else if (dateRange === "custom" && startDate && endDate) {
      start = new Date(startDate);
      end = new Date(endDate);
    }

    const inRange = (dt) => {
      if (!start || !end) return true; // "all" or invalid dates -> no filter
      const d = new Date(dt);
      if (Number.isNaN(d.getTime())) return false;
      return d >= start && d <= end;
    };

    const filtered = parties.filter((p) => inRange(p.createdAt || p.updatedAt || p.created));
    setFilteredParties(filtered);
  }, [parties, dateRange, startDate, endDate]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearErrors());
        dispatch(clearMessage());
        dispatch(resetSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "number") {
      // Only allow digits, max 10
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      if (touched[name]) {
        validateField(name, digitsOnly);
      }
    } else if (name === "duePayment" || name === "totalPayment" || name === "advanceAmount") {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, "");
      const parts = numericValue.split(".");
      const finalValue = parts.length > 2 
        ? parts[0] + "." + parts.slice(1).join("")
        : numericValue;
      
      setFormData((prev) => ({ ...prev, [name]: finalValue }));
      if (touched[name]) {
        validateField(name, finalValue);
        
        // Validate advanceAmount against totalPayment in real-time
        if (name === "advanceAmount" && formData.totalPayment) {
          const totalPaymentNum = parseFloat(formData.totalPayment);
          const advanceAmountNum = parseFloat(finalValue);
          if (advanceAmountNum > totalPaymentNum) {
            setErrors((prev) => ({
              ...prev,
              advanceAmount: "Advance amount cannot exceed total payment",
            }));
          }
        } else if (name === "totalPayment" && formData.advanceAmount) {
          const totalPaymentNum = parseFloat(finalValue);
          const advanceAmountNum = parseFloat(formData.advanceAmount);
          if (advanceAmountNum > totalPaymentNum) {
            setErrors((prev) => ({
              ...prev,
              advanceAmount: "Advance amount cannot exceed total payment",
            }));
          }
        }
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (touched[name]) {
        validateField(name, value);
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const partyData = {
      name: formData.name.trim(),
      number: formData.number.replace(/\D/g, ""),
      duePayment: parseFloat(formData.duePayment),
      totalPayment: parseFloat(formData.totalPayment),
      advanceAmount: parseFloat(formData.advanceAmount),
    };

    try {
      if (isEditing) {
        await dispatch(updateParty({ id: editingId, partyData })).unwrap();
      } else {
        await dispatch(createParty(partyData)).unwrap();
      }
      
      resetForm();
      setShowModal(false);
      if (showMyParties && user) {
        dispatch(getMyParties());
      } else {
        dispatch(getAllParties());
      }
    } catch (error) {
      // Error handled by Redux
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      number: "",
      duePayment: "",
      totalPayment: "",
      advanceAmount: "",
    });
    setErrors({
      name: "",
      number: "",
      duePayment: "",
      totalPayment: "",
      advanceAmount: "",
    });
    setTouched({
      name: false,
      number: false,
      duePayment: false,
      totalPayment: false,
      advanceAmount: false,
    });
    setIsEditing(false);
    setEditingId(null);
  };

  // Handle edit
  const handleEdit = (party) => {
    setFormData({
      name: party.name,
      number: party.number,
      duePayment: party.duePayment.toString(),
      totalPayment: party.totalPayment.toString(),
      advanceAmount: party.advanceAmount.toString(),
    });
    setIsEditing(true);
    setEditingId(party._id);
    setShowModal(true);
    setErrors({
      name: "",
      number: "",
      duePayment: "",
      totalPayment: "",
      advanceAmount: "",
    });
    setTouched({
      name: false,
      number: false,
      duePayment: false,
      totalPayment: false,
      advanceAmount: false,
    });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this party?")) {
      try {
        await dispatch(deleteParty(id)).unwrap();
        if (showMyParties && user) {
          dispatch(getMyParties());
        } else {
          dispatch(getAllParties());
        }
      } catch (error) {
        // Error handled by Redux
      }
    }
  };

  // Handle search
  const handleSearch = useCallback(
    async (query) => {
      setSearchQuery(query);
      if (query.trim()) {
        dispatch(searchParties(query));
      } else {
        if (showMyParties && user) {
          dispatch(getMyParties());
        } else {
          dispatch(getAllParties());
        }
      }
    },
    [dispatch, showMyParties, user]
  );

  // Toggle between all parties and my parties
  const handleToggleView = () => {
    setShowMyParties(!showMyParties);
    setSearchQuery("");
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== "custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  // PDF download for current filtered parties
  const handleDownload = () => {
    if (!filteredParties || filteredParties.length === 0) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Party Summary", 105, 14, { align: "center" });

    doc.setFontSize(10);
    let dateInfo = "All Dates";
    if (dateRange === "thisMonth") {
      const r = getDateRanges();
      dateInfo = `Date: ${formatDate(r.thisMonth.start)} to ${formatDate(r.thisMonth.end)}`;
    } else if (dateRange === "lastMonth") {
      const r = getDateRanges();
      dateInfo = `Date: ${formatDate(r.lastMonth.start)} to ${formatDate(r.lastMonth.end)}`;
    } else if (dateRange === "custom" && startDate && endDate) {
      dateInfo = `Date: ${formatDate(startDate)} to ${formatDate(endDate)}`;
    }
    doc.text(dateInfo, 14, 22);
    doc.text(`Generated: ${formatDate(new Date())}`, 14, 28);

    const body = filteredParties.map((p, idx) => [
      idx + 1,
      p.name || "N/A",
      p.number || "N/A",
      formatMoney(p.totalPayment),
      formatMoney(p.advanceAmount),
      formatMoney(p.duePayment),
      formatDate(p.createdAt),
    ]);

    const totals = filteredParties.reduce(
      (acc, p) => {
        acc.total += parseFloat(p.totalPayment || 0);
        acc.advance += parseFloat(p.advanceAmount || 0);
        acc.due += parseFloat(p.duePayment || 0);
        return acc;
      },
      { total: 0, advance: 0, due: 0 }
    );

    body.push([
      "—",
      "TOTAL",
      "",
      formatMoney(totals.total),
      formatMoney(totals.advance),
      formatMoney(totals.due),
      "",
    ]);

    autoTable(doc, {
      startY: 34,
      head: [["#", "Name", "Number", "Total", "Advance", "Due", "Created"]],
      body,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [102, 126, 234], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    const fileName = `Party_Summary_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  const showToast = useCallback((message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `party-toast party-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("party-toast-show"), 100);
    setTimeout(() => {
      toast.classList.remove("party-toast-show");
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  }, []);

  useEffect(() => {
    if (success && message) {
      showToast(message, "success");
    }
    if (error) {
      showToast(error, "error");
    }
  }, [success, error, message, showToast]);


  return (
    <>
      <Helmet>
        <title>Party - RTS Books</title>
      </Helmet>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes partyFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes partySlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes partyShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .party-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          background-size: 400% 400%;
          animation: partyFadeIn 0.6s ease;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .party-wrapper {
          max-width: 1400px;
          margin: 0 auto;
        }

        .party-header {
          text-align: center;
          margin-bottom: 30px;
          color: white;
        }

        .party-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .party-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin-bottom: 20px;
        }

        .party-search-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .party-search-input {
          flex: 1;
          min-width: 200px;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .party-search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .party-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .party-btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .party-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .party-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .party-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }

        .party-select {
          padding: 10px 14px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          background: white;
          color: #1e293b;
          min-width: 150px;
        }

        .party-select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .party-table-wrapper::-webkit-scrollbar {
          height: 8px;
        }

        .party-table-wrapper::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .party-table-wrapper::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 10px;
        }

        .party-table-wrapper::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .party-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          min-width: 600px;
        }

        .party-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .party-table th {
          padding: 16px;
          text-align: left;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .party-table td {
          padding: 16px;
          border-bottom: 1px solid #e1e5e9;
          vertical-align: middle;
        }

        .party-table tbody tr:hover {
          background: #f8f9fa;
        }

        .party-table tbody tr:last-child td {
          border-bottom: none;
        }

        .party-amount {
          text-align: right;
          font-weight: 600;
          color: #1e293b;
          white-space: nowrap;
        }

        .party-action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 8px;
        }

        .party-btn-edit {
          background: #10b981;
          color: white;
        }

        .party-btn-edit:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .party-btn-delete {
          background: #ef4444;
          color: white;
        }

        .party-btn-delete:hover {
          background: #dc2626;
          transform: translateY(-1px);
        }

        .party-balance {
          font-weight: 600;
          font-size: 1rem;
        }

        .party-balance.positive {
          color: #dc2626;
        }

        .party-balance.negative {
          color: #059669;
        }

        .party-advance-amount {
          color: #059669;
          font-weight: 600;
        }

        .party-due-amount {
          color: #dc2626;
          font-weight: 600;
        }

        .party-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .party-modal-content {
          background: white;
          border-radius: 20px;
          padding: 30px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: partySlideIn 0.3s ease;
        }

        .party-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .party-modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .party-modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .party-modal-close:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .party-form-group {
          margin-bottom: 20px;
        }

        .party-label {
          display: block;
          margin-bottom: 8px;
          color: #334155;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .party-label.required::after {
          content: " *";
          color: #dc2626;
        }

        .party-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .party-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .party-input.error {
          border-color: #dc2626;
          animation: partyShake 0.5s ease;
        }

        .party-error {
          color: #dc2626;
          font-size: 0.85rem;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .party-modal-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .party-btn-secondary {
          background: #e1e5e9;
          color: #334155;
        }

        .party-btn-secondary:hover {
          background: #cbd5e1;
        }

        .party-btn-toggle {
          background: #10b981;
          color: white;
        }

        .party-btn-toggle:hover {
          background: #059669;
        }

        .party-user-badge {
          display: inline-block;
          padding: 4px 8px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .party-loading {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .party-empty {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .party-toast {
          position: fixed;
          top: 20px;
          right: -300px;
          background: #fff;
          color: #333;
          padding: 15px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          transition: all 0.3s ease;
          max-width: 350px;
          font-weight: 500;
        }

        .party-toast.party-toast-success {
          border-left: 4px solid #059669;
        }

        .party-toast.party-toast-error {
          border-left: 4px solid #dc2626;
        }

        .party-toast.party-toast-show {
          right: 20px;
          animation: partySlideIn 0.3s ease;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .party-container {
            padding: 15px;
          }

          .party-title {
            font-size: 2rem;
          }

          .party-card {
            padding: 20px;
            border-radius: 16px;
          }

          .party-search-bar {
            flex-direction: column;
          }

          .party-search-input {
            min-width: 100%;
          }

          .party-table {
            font-size: 0.9rem;
            min-width: 900px;
          }

          .party-table th,
          .party-table td {
            padding: 12px 8px;
            white-space: nowrap;
          }

          .party-table th:nth-child(3),
          .party-table td:nth-child(3) {
            min-width: 100px;
          }

          .party-balance {
            font-size: 0.95rem;
          }

          .party-action-btn {
            padding: 6px 12px;
            font-size: 0.85rem;
            margin-right: 4px;
            margin-bottom: 4px;
          }

          .party-modal-content {
            padding: 20px;
            margin: 10px;
          }

          .party-modal-title {
            font-size: 1.3rem;
          }
        }

        @media (max-width: 480px) {
          .party-title {
            font-size: 1.5rem;
          }

          .party-card {
            padding: 15px;
          }

          .party-table {
            font-size: 0.85rem;
            min-width: 750px;
          }

          .party-table th,
          .party-table td {
            padding: 10px 6px;
            white-space: nowrap;
          }

          .party-table th:nth-child(4),
          .party-table td:nth-child(4),
          .party-table th:nth-child(5),
          .party-table td:nth-child(5) {
            display: none;
          }

          .party-table th:nth-child(3),
          .party-table td:nth-child(3) {
            min-width: 90px;
          }

          .party-balance {
            font-size: 0.9rem;
            font-weight: 700;
          }

          .party-action-btn {
            padding: 6px 10px;
            font-size: 0.8rem;
          }

          .party-modal-content {
            padding: 15px;
          }

          .party-btn {
            padding: 10px 16px;
            font-size: 0.9rem;
          }
        }

        @media (max-width: 360px) {
          .party-table {
            min-width: 650px;
          }

          .party-table th,
          .party-table td {
            padding: 8px 4px;
            font-size: 0.8rem;
            white-space: nowrap;
          }

          .party-table th:nth-child(3),
          .party-table td:nth-child(3) {
            min-width: 80px;
          }

          .party-balance {
            font-size: 0.85rem;
            font-weight: 700;
          }

          .party-action-btn {
            padding: 4px 8px;
            font-size: 0.75rem;
          }
        }
      `}</style>

      <div className="party-container">
        <div className="party-wrapper">
          <div className="party-header">
            <h1 className="party-title">Party Management</h1>
          </div>

          <div className="party-card">
            <div className="party-search-bar">
              <input
                type="text"
                className="party-search-input"
                placeholder="Search parties by name or number..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <select
                className="party-select"
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
              >
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="all">All Dates</option>
                <option value="custom">Custom Range</option>
              </select>
              {dateRange === "custom" && (
                <>
                  <input
                    type="date"
                    className="party-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <input
                    type="date"
                    className="party-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </>
              )}
              {user && (
                <button
                  className="party-btn party-btn-toggle"
                  onClick={handleToggleView}
                >
                  {showMyParties ? "Show All" : "Show My Parties"}
                </button>
              )}
              <button
                className="party-btn party-btn-primary"
                onClick={handleDownload}
                disabled={!filteredParties || filteredParties.length === 0}
              >
                📥 Download
              </button>
              <button
                className="party-btn party-btn-primary"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                + Add Party
              </button>
            </div>

            {loading && <div className="party-loading">Loading...</div>}

            {!loading && filteredParties.length === 0 && (
              <div className="party-empty">No parties found</div>
            )}

            {!loading && filteredParties.length > 0 && (
              <div className="party-table-wrapper">
                <table className="party-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Number</th>
                      <th>Total Payment</th>
                      <th>Advance</th>
                      <th>Due</th>
                      <th>Created By</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredParties.map((party) => {
                      return (
                        <tr key={party._id}>
                          <td>{party.name}</td>
                          <td>{party.number}</td>
                          <td className="party-amount">
                            Rs.{parseFloat(party.totalPayment || 0).toFixed(2)}
                          </td>
                          <td className="party-amount">
                            Rs.{parseFloat(party.advanceAmount || 0).toFixed(2)}
                          </td>
                          <td className="party-amount">
                            Rs.{parseFloat(party.duePayment || 0).toFixed(2)}
                          </td>
                          <td>
                            {party.user ? (
                              <span className="party-user-badge">
                                {party.user.name || party.user.phone || "Unknown"}
                              </span>
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td>
                            {new Date(party.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <button
                              className="party-action-btn party-btn-edit"
                              onClick={() => handleEdit(party)}
                            >
                              Edit
                            </button>
                            <button
                              className="party-action-btn party-btn-delete"
                              onClick={() => handleDelete(party._id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="party-modal" onClick={() => setShowModal(false)}>
          <div
            className="party-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="party-modal-header">
              <h2 className="party-modal-title">
                {isEditing ? "Edit Party" : "Add New Party"}
              </h2>
              <button
                className="party-modal-close"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="party-form-group">
                <label className="party-label required" htmlFor="name">
                  Party Name
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("name")}
                  placeholder="Enter party name"
                  className={`party-input ${
                    errors.name && touched.name ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.name && touched.name && (
                  <div className="party-error">⚠️ {errors.name}</div>
                )}
              </div>

              <div className="party-form-group">
                <label className="party-label required" htmlFor="number">
                  Party Number
                </label>
                <input
                  id="number"
                  type="tel"
                  name="number"
                  value={formData.number}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("number")}
                  placeholder="Enter 10-digit number"
                  maxLength={10}
                  className={`party-input ${
                    errors.number && touched.number ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.number && touched.number && (
                  <div className="party-error">⚠️ {errors.number}</div>
                )}
              </div>

              <div className="party-form-group">
                <label className="party-label required" htmlFor="totalPayment">
                  Total Payment
                </label>
                <input
                  id="totalPayment"
                  type="text"
                  name="totalPayment"
                  value={formData.totalPayment}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("totalPayment")}
                  placeholder="Enter total payment (e.g., 10000.50)"
                  className={`party-input ${
                    errors.totalPayment && touched.totalPayment ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.totalPayment && touched.totalPayment && (
                  <div className="party-error">⚠️ {errors.totalPayment}</div>
                )}
              </div>

              <div className="party-form-group">
                <label className="party-label required" htmlFor="advanceAmount">
                  Advance Amount
                </label>
                <input
                  id="advanceAmount"
                  type="text"
                  name="advanceAmount"
                  value={formData.advanceAmount}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("advanceAmount")}
                  placeholder="Enter advance amount (e.g., 5000.00)"
                  className={`party-input ${
                    errors.advanceAmount && touched.advanceAmount ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.advanceAmount && touched.advanceAmount && (
                  <div className="party-error">⚠️ {errors.advanceAmount}</div>
                )}
              </div>

              <div className="party-form-group">
                <label className="party-label required" htmlFor="duePayment">
                  Due Payment
                </label>
                <input
                  id="duePayment"
                  type="text"
                  name="duePayment"
                  value={formData.duePayment}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("duePayment")}
                  placeholder="Enter due payment (e.g., 2000.00)"
                  className={`party-input ${
                    errors.duePayment && touched.duePayment ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.duePayment && touched.duePayment && (
                  <div className="party-error">⚠️ {errors.duePayment}</div>
                )}
              </div>

              <div className="party-modal-actions">
                <button
                  type="button"
                  className="party-btn party-btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="party-btn party-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Party;

